import { useEffect } from 'react';
import { initializeSdk } from '@eventbrite/legacy-infra.statsig';

import { getUser, getOptions, shouldInitializeSDK } from './utils';
import type { StatsigConfiguration } from './types';

let initialized = false;
const initCallbacks: Array<() => void> = [];

/**
 * Register a callback to be executed when Statsig is initialized
 */
export const onStatsigReady = (callback: () => void) => {
    if (initialized) {
        console.info('[statsig] Already initialized, executing callback immediately');
        callback();
    } else {
        console.warn('[statsig] Not initialized yet, storing callback for later');
        initCallbacks.push(callback);
    }
};

/**
 * Initialize Statsig client
 */
export const useInitStatsig = ({
    clientKey,
    sampleRate,
    deactivateSdk,
    user,
    userFields,
    options,
}: StatsigConfiguration): void => {
    useEffect(() => {
        if (initialized || !shouldInitializeSDK(sampleRate, deactivateSdk)) {
            return;
        }

        const initialize = async () => {
            try {
                const statsigUser = getUser(user, userFields);
                const statsigOptions = getOptions(options);

                await initializeSdk({
                    clientKey,
                    user: statsigUser,
                    options: statsigOptions,
                });
                initialized = true;

                while (initCallbacks.length > 0) {
                    const callback = initCallbacks.shift();
                    if (callback) callback();
                }
            } catch (error) {
                // Ignore errors thrown during Statsig initialization
                console.error('Failed to initialize Statsig:', error);
            }
        };

        initialize();
    }, [clientKey, sampleRate, deactivateSdk, user, userFields, options]);
};
